import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getMySpaceBookmarks,
  removeSpaceBookmark,
  type SpaceBookmarkItem,
} from '../api/bookmarks';
import { ApiError } from '../api/client';
import { fetchVendorDetailById } from '../api/spaces';
import { HomeFooter } from '../components/home/HomeFooter';
import { HomeHeader } from '../components/home/HomeHeader';
import { useGuestGate } from '../components/home/GuestGateProvider';
import { useToast } from '../components/ui/ToastProvider';
import { HEADER_SEARCH_KEYWORD_SUGGESTIONS } from '../config/searchSuggestions';
import { isMockMode } from '../config/publicEnv';
import { loadAuthSession } from '../data/authSession';
import {
  MY_SCRAP_RECENT,
  MY_SCRAP_SAVED,
  type MyScrapCard as MyScrapCardModel,
} from '../data/myScraps';

type TabKey = 'saved' | 'recent';

const PAGE_SIZE = 20;

function ScrapBookmarkGlyph({ filled }: { filled: boolean }) {
  return (
    <svg aria-hidden fill="none" height={28} viewBox="0 0 28 28" width={28}>
      <path
        d="M9 6.5C9 5.67 9.67 5 10.5 5H17.5C18.33 5 19 5.67 19 6.5V22L14 18.3L9 22V6.5Z"
        fill={filled ? '#24262c' : 'rgba(0,0,0,0.36)'}
        stroke={filled ? '#24262c' : 'rgba(255,255,255,0.75)'}
        strokeLinejoin="round"
        strokeWidth={1.5}
      />
    </svg>
  );
}

function ScrapStarGlyph() {
  return (
    <svg aria-hidden fill="none" height={14} viewBox="0 0 14 14" width={14}>
      <path
        d="M7 1.8l1.65 4.05 4.35.34-3.35 2.84 1.04 4.24L7 11.12l-3.69 2.15L4.35 9l-3.35-2.84 4.35-.34L7 1.8z"
        fill="#FFA20C"
        stroke="#FFA20C"
        strokeLinejoin="round"
        strokeWidth={0.8}
      />
    </svg>
  );
}

function formatSavedDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const year = String(parsed.getFullYear()).slice(-2);
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}.${month}.${day} 저장`;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

/** 실서버 북마크(스튜디오 단위) 카드 — GET /users/me/bookmarks/spaces 응답 필드만 사용. */
function BookmarkCard({
  item,
  onOpen,
  onRemove,
  removing,
}: {
  item: SpaceBookmarkItem;
  onOpen: (item: SpaceBookmarkItem) => void;
  onRemove: (item: SpaceBookmarkItem) => void;
  removing: boolean;
}) {
  return (
    <article className="my-scraps-card">
      <div className="my-scraps-card__thumb-wrap">
        <button
          aria-label={`${item.name} 상세 보기`}
          className="my-scraps-card__thumb-link"
          onClick={() => onOpen(item)}
          type="button"
        >
          <div aria-hidden className="my-scraps-card__thumb my-scraps-card__thumb--placeholder" />
        </button>
        <button
          aria-label="스크랩 해제"
          aria-pressed
          className="my-scraps-card__scrap"
          disabled={removing}
          onClick={() => onRemove(item)}
          type="button"
        >
          <ScrapBookmarkGlyph filled />
        </button>
      </div>

      <div className="my-scraps-card__body">
        <div className="my-scraps-card__head">
          <button
            className="my-scraps-card__title-link"
            onClick={() => onOpen(item)}
            type="button"
          >
            <h3 className="my-scraps-card__title">{item.name}</h3>
          </button>
          <div className="my-scraps-card__meta">
            <span className="my-scraps-card__location">{item.roadAddress ?? ''}</span>
          </div>
        </div>
        <div className="my-scraps-card__foot">
          <span className="my-scraps-card__chip">{formatSavedDate(item.createdAt)}</span>
        </div>
      </div>
    </article>
  );
}

/** 목모드 전용 데모 카드 — 기존 피그마 시안 유지. */
function MockScrapCard({
  item,
  onToggleScrap,
}: {
  item: MyScrapCardModel;
  onToggleScrap: (detailPath: string) => void;
}) {
  return (
    <article className="my-scraps-card">
      <div className="my-scraps-card__thumb-wrap">
        <Link className="my-scraps-card__thumb-link" to={item.detailPath}>
          <img alt="" className="my-scraps-card__thumb" loading="lazy" src={item.imageUrl} />
        </Link>
        <button
          aria-label={item.bookmarkSaved ? '스크랩 해제' : '스크랩'}
          aria-pressed={item.bookmarkSaved}
          className="my-scraps-card__scrap"
          onClick={() => onToggleScrap(item.detailPath)}
          type="button"
        >
          <ScrapBookmarkGlyph filled={item.bookmarkSaved} />
        </button>
      </div>

      <div className="my-scraps-card__body">
        <div className="my-scraps-card__head">
          <div className="my-scraps-card__tags">
            <span className="my-scraps-card__tag">{item.spaceType}</span>
            <span aria-hidden className="my-scraps-card__dot" />
            <span className="my-scraps-card__tag">{item.studio}</span>
          </div>
          <Link className="my-scraps-card__title-link" to={item.detailPath}>
            <h3 className="my-scraps-card__title">{item.title}</h3>
          </Link>
          <div className="my-scraps-card__meta">
            <span className="my-scraps-card__rating">
              <ScrapStarGlyph />
              <span className="my-scraps-card__rating-num">{item.rating}</span>
            </span>
            <span aria-hidden className="my-scraps-card__dot my-scraps-card__dot--muted" />
            <span className="my-scraps-card__location">{item.location}</span>
          </div>
        </div>

        <div className="my-scraps-card__foot">
          <div className="my-scraps-card__price">
            <span className="my-scraps-card__price-value">{item.priceLabel}</span>
            <span className="my-scraps-card__price-suffix">{item.priceSuffix}</span>
          </div>
          <div className="my-scraps-card__chips">
            <span className="my-scraps-card__chip">{item.tags[0]}</span>
            <span aria-hidden className="my-scraps-card__dot" />
            <span className="my-scraps-card__chip">{item.tags[1]}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function useMockScraps() {
  const catalog = useMemo<Record<string, MyScrapCardModel>>(() => {
    const map: Record<string, MyScrapCardModel> = {};
    for (const row of [...MY_SCRAP_SAVED, ...MY_SCRAP_RECENT]) {
      if (!map[row.detailPath]) map[row.detailPath] = row;
    }
    return map;
  }, []);

  const recentOrder = useMemo(() => MY_SCRAP_RECENT.map((row) => row.detailPath), []);

  const [savedOrder, setSavedOrder] = useState<string[]>(() =>
    MY_SCRAP_SAVED.filter((row) => row.bookmarkSaved).map((row) => row.detailPath),
  );
  const [savedSet, setSavedSet] = useState<Set<string>>(
    () => new Set(MY_SCRAP_SAVED.filter((row) => row.bookmarkSaved).map((row) => row.detailPath)),
  );

  const toggleScrap = useCallback((detailPath: string) => {
    setSavedSet((prev) => {
      const next = new Set(prev);
      if (next.has(detailPath)) {
        next.delete(detailPath);
      } else {
        next.add(detailPath);
      }
      return next;
    });
    setSavedOrder((prev) => {
      if (prev.includes(detailPath)) {
        return prev.filter((p) => p !== detailPath);
      }
      return [detailPath, ...prev];
    });
  }, []);

  const savedItems = useMemo<MyScrapCardModel[]>(
    () =>
      savedOrder
        .map((path) => catalog[path])
        .filter((row): row is MyScrapCardModel => Boolean(row))
        .map((row) => ({ ...row, bookmarkSaved: true })),
    [catalog, savedOrder],
  );

  const recentItems = useMemo<MyScrapCardModel[]>(
    () =>
      recentOrder.map((path) => {
        const row = catalog[path];
        return { ...row, bookmarkSaved: savedSet.has(path) };
      }),
    [catalog, recentOrder, savedSet],
  );

  return { savedItems, recentItems, toggleScrap };
}

export function MyScrapsPage() {
  const navigate = useNavigate();
  const { openGuestGate } = useGuestGate();
  const { showToast } = useToast();
  const mock = isMockMode();
  const isAuthenticated = Boolean(loadAuthSession());

  const [headerSearchOpen, setHeaderSearchOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const headerSearchRef = useRef<HTMLDivElement | null>(null);

  const [activeTab, setActiveTab] = useState<TabKey>('saved');

  const mockScraps = useMockScraps();

  // --- 실서버 북마크 목록 상태 ---
  const [bookmarks, setBookmarks] = useState<SpaceBookmarkItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(!mock);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadBookmarks = useCallback(
    async (nextCursor?: string) => {
      try {
        const page = await getMySpaceBookmarks({
          cursor: nextCursor,
          size: PAGE_SIZE,
        });
        setBookmarks((prev) => (nextCursor ? [...prev, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setHasNext(Boolean(page.hasNext && page.nextCursor));
        setErrorMessage('');
      } catch (error) {
        setErrorMessage(getErrorMessage(error, '스크랩 목록을 불러오지 못했습니다.'));
      }
    },
    [],
  );

  useEffect(() => {
    if (mock) {
      return;
    }
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadBookmarks().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loadBookmarks, mock]);

  const handleLoadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    await loadBookmarks(cursor);
    setLoadingMore(false);
  }, [cursor, loadBookmarks, loadingMore]);

  const handleRemove = useCallback(
    async (item: SpaceBookmarkItem) => {
      if (removingId) return;
      setRemovingId(item.bookmarkId);
      try {
        await removeSpaceBookmark(item.spaceId);
        setBookmarks((prev) => prev.filter((row) => row.bookmarkId !== item.bookmarkId));
        showToast('스크랩을 해제했습니다.');
      } catch (error) {
        showToast(getErrorMessage(error, '스크랩 해제에 실패했습니다.'));
      } finally {
        setRemovingId(null);
      }
    },
    [removingId, showToast],
  );

  const handleOpen = useCallback(
    async (item: SpaceBookmarkItem) => {
      try {
        const vendor = await fetchVendorDetailById(item.spaceId);
        if (vendor.slug) {
          navigate(`/vendors/${encodeURIComponent(vendor.slug)}`);
          return;
        }
      } catch {
        // fallthrough — 검색으로 대체 이동
      }
      navigate(`/search?q=${encodeURIComponent(item.name)}`);
    },
    [navigate],
  );

  const filteredSuggestions = useMemo(
    () =>
      HEADER_SEARCH_KEYWORD_SUGGESTIONS.filter((item) =>
        item.toLowerCase().includes(headerSearchQuery.toLowerCase()),
      ),
    [headerSearchQuery],
  );

  const onHeaderSearchSubmit = useCallback(
    (value: string) => {
      const q = value.trim();
      if (!q) return;
      navigate(`/search?q=${encodeURIComponent(q)}`);
    },
    [navigate],
  );

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!headerSearchRef.current?.contains(event.target as Node)) {
        setHeaderSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const savedCountLabel = mock ? mockScraps.savedItems.length : bookmarks.length;
  const recentCountLabel = mock ? mockScraps.recentItems.length : 0;

  return (
    <main className="my-scraps-page">
      <HomeHeader
        authenticated={isAuthenticated}
        filteredSuggestions={filteredSuggestions}
        onGuestCta={() => navigate('/login')}
        onSearchChange={(value) => {
          setHeaderSearchQuery(value);
          setHeaderSearchOpen(Boolean(value.trim()));
        }}
        onSearchClear={() => {
          setHeaderSearchQuery('');
          setHeaderSearchOpen(false);
        }}
        onSearchFocus={() => setHeaderSearchOpen(Boolean(headerSearchQuery.trim()))}
        onSearchSubmit={onHeaderSearchSubmit}
        onSuggestionSelect={(value) => {
          setHeaderSearchOpen(false);
          onHeaderSearchSubmit(value);
        }}
        searchOpen={headerSearchOpen}
        searchQuery={headerSearchQuery}
        searchRef={headerSearchRef}
      />

      <div className="my-scraps-page__main">
        <section className="my-scraps">
          <h1 className="my-scraps__title">내 스크랩</h1>

          <div className="my-scraps__tabs" role="tablist">
            <button
              aria-selected={activeTab === 'saved'}
              className={`my-scraps__tab${activeTab === 'saved' ? ' my-scraps__tab--active' : ''}`}
              onClick={() => setActiveTab('saved')}
              role="tab"
              type="button"
            >
              저장 {savedCountLabel}
            </button>
            <button
              aria-selected={activeTab === 'recent'}
              className={`my-scraps__tab${activeTab === 'recent' ? ' my-scraps__tab--active' : ''}`}
              onClick={() => setActiveTab('recent')}
              role="tab"
              type="button"
            >
              최근 본 {recentCountLabel}
            </button>
          </div>

          {mock ? (
            (activeTab === 'saved' ? mockScraps.savedItems : mockScraps.recentItems).length ===
            0 ? (
              <p className="my-scraps__empty">
                {activeTab === 'saved' ? '저장한 공간이 없습니다.' : '최근에 본 공간이 없습니다.'}
              </p>
            ) : (
              <div className="my-scraps__grid" role="tabpanel">
                {(activeTab === 'saved' ? mockScraps.savedItems : mockScraps.recentItems).map(
                  (item) => (
                    <MockScrapCard
                      item={item}
                      key={item.id}
                      onToggleScrap={mockScraps.toggleScrap}
                    />
                  ),
                )}
              </div>
            )
          ) : activeTab === 'recent' ? (
            <p className="my-scraps__empty">최근에 본 공간이 없습니다.</p>
          ) : !isAuthenticated ? (
            <p className="my-scraps__empty">
              <button
                className="my-scraps__login-cta"
                onClick={() => openGuestGate()}
                type="button"
              >
                로그인하고 저장한 공간을 확인해보세요.
              </button>
            </p>
          ) : loading ? (
            <p className="my-scraps__empty">불러오는 중…</p>
          ) : errorMessage ? (
            <p className="my-scraps__empty" role="alert">
              {errorMessage}
            </p>
          ) : bookmarks.length === 0 ? (
            <p className="my-scraps__empty">저장한 공간이 없습니다.</p>
          ) : (
            <>
              <div className="my-scraps__grid" role="tabpanel">
                {bookmarks.map((item) => (
                  <BookmarkCard
                    item={item}
                    key={item.bookmarkId}
                    onOpen={handleOpen}
                    onRemove={handleRemove}
                    removing={removingId === item.bookmarkId}
                  />
                ))}
              </div>
              {hasNext ? (
                <button
                  className="my-scraps__load-more"
                  disabled={loadingMore}
                  onClick={handleLoadMore}
                  type="button"
                >
                  {loadingMore ? '불러오는 중…' : '더보기'}
                </button>
              ) : null}
            </>
          )}
        </section>
      </div>

      <HomeFooter />
    </main>
  );
}
